import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Globe, Bell } from 'lucide-react';
import axios from '../../axiosConfig';
import Badge from '../../components/ui/Badge';
import { getStoreUrl } from '../../config/platformConfig';
import ChangelogDropdown from './ChangelogDropdown';
import HeaderOverflowMenu from './HeaderOverflowMenu';
import './HeaderActions.css';

/**
 * Topbar action cluster.
 *
 * Desktop (>768px): View Storefront link · ChangelogDropdown (Sparkles) ·
 *   Notifications (Bell).
 * Mobile (≤768px): View Storefront link (≥480px only) · HeaderOverflowMenu —
 *   the kebab menu folds Sparkles + Bell into one popover. Mobile header
 *   strip was wrapping with both inline; consolidating frees the row.
 *
 * Visibility is CSS-gated via `.ha-desktop-only` / `.ha-mobile-only`
 * (HeaderOverflowMenu.css). Both render in JSX so the mounted/unmounted
 * state of the components stays stable across resizes.
 */
const HeaderActions = () => {
  const navigate = useNavigate();
  const store = useSelector((s) => s.store?.current);
  const notifications = useSelector((s) => s.notifications?.items || []);
  const unread = notifications.filter((n) => !n.isRead).length;

  const [verifiedDomain, setVerifiedDomain] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/custom-domains/merchant');
        if (cancelled) return;
        const domains = res.data?.data || [];
        const verified = domains.find((d) => d.status === 'VERIFIED');
        if (verified) setVerifiedDomain(verified);
      } catch {
        // Silent — falls back to store-slug URL.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const storefrontUrl = verifiedDomain
    ? `https://${verifiedDomain.domain}`
    : (store?.slug ? getStoreUrl(store.slug) : null);

  return (
    <div className="header-actions">
      {storefrontUrl && (
        <a
          href={storefrontUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="header-action-btn"
          title={verifiedDomain ? `View Storefront (${verifiedDomain.domain})` : 'View Storefront'}
          aria-label="View storefront"
        >
          <Globe size={18} aria-hidden="true" />
        </a>
      )}

      {/* Desktop only — hidden ≤768px via .ha-desktop-only */}
      <ChangelogDropdown />

      <button
        type="button"
        className="header-action-btn header-action-bell ha-desktop-only"
        onClick={() => navigate('/notifications')}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
      >
        <Bell size={18} aria-hidden="true" />
        {unread > 0 && (
          <Badge variant="error" size="sm" className="header-action-bell-badge">
            {unread > 99 ? '99+' : unread}
          </Badge>
        )}
      </button>

      {/* Mobile only — hidden >768px via .ha-mobile-only */}
      <HeaderOverflowMenu />
    </div>
  );
};

export default HeaderActions;
